import React from 'react'
import { Box, Chip, FormControlLabel, Switch, Typography, alpha } from '@mui/material'

export interface ToggleGatedSectionProps {
  /** Label beside the gating switch. */
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  /** Short helper text under the toggle inside the participation card. */
  description?: React.ReactNode
  /** Rendered when the toggle is off (e.g. an info empty-state). */
  offState: React.ReactNode
  /** Rendered when the toggle is on. */
  children: React.ReactNode
  /**
   * Render the toggle row as an emphasised "master setting" banner: a primary
   * left accent, a tint + stronger heading when on, and an On/Off status chip.
   * Use when the toggle governs the whole page/tab, not one nested section.
   */
  emphasis?: boolean
}

/**
 * A labelled switch that gates a block of content: the toggle sits in its own
 * participation card (with optional helper text), `children` show when on and
 * an `offState` empty-state shows when off. Keeps "this section only applies
 * when enabled" UX consistent across admin forms - see
 * docs/development/admin-forms.md.
 */
const ToggleGatedSection: React.FC<ToggleGatedSectionProps> = ({
  label,
  checked,
  onChange,
  description,
  offState,
  children,
  emphasis = false,
}) => (
  <Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 0.5,
        px: 2,
        py: emphasis ? 1.75 : 1.25,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: emphasis && checked ? 'primary.main' : 'divider',
        bgcolor:
          emphasis && checked ? (t) => alpha(t.palette.primary.main, 0.08) : 'grey.50',
        ...(emphasis && {
          borderLeftWidth: 4,
          borderLeftStyle: 'solid',
          borderLeftColor: checked ? 'primary.main' : 'divider',
        }),
      }}
    >
      <FormControlLabel
        sx={{ mr: 0 }}
        control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />}
        label={
          <Typography sx={{ fontWeight: emphasis ? 700 : 600, fontSize: emphasis ? '1rem' : undefined }}>
            {label}
          </Typography>
        }
      />
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
      {emphasis && (
        <Chip
          size="small"
          label={checked ? 'On' : 'Off'}
          color={checked ? 'primary' : 'default'}
          variant={checked ? 'filled' : 'outlined'}
          sx={{ ml: 'auto', fontWeight: 600 }}
        />
      )}
    </Box>
    <Box sx={{ mt: 3 }}>{checked ? children : offState}</Box>
  </Box>
)

export default ToggleGatedSection
